import assetController from "@/services/assetService";
import deviceTypeController from "@/services/deviceTypeService";
import employeeController from "@/services/employeeService";
import corporateController from "@/services/corporateService";
import siteController from "@/services/siteService";
import divisionController from "@/services/divisionService";
import positionController from "@/services/positionService";
import parameterController from "@/services/parameterService";
import Swal from "sweetalert2";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";

const toUpper = (v) =>
  String(v ?? "")
    .trim()
    .toUpperCase();

// Hanya data aktif (status = 1). Kalau field status tidak ada di data, dianggap aktif.
const isActive = (x) => {
  const val = x.status ?? x.is_active;
  return val === undefined || val === null ? true : Number(val) === 1;
};

export default {
  name: "AssetFormView",

  components: { Multiselect },

  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ["add", "edit", "detail"].includes(v),
    },
  },

  data() {
    return {
      isLoading: false,

      deviceTypeList: [],
      employeeList: [],
      corporateList: [],
      siteList: [],
      divisionList: [],
      positionList: [],
      coaList: [],
      conditionList: [],
      documentTypeList: [],
      existingDocuments: [],
      newDocuments: [], // { file, document_type }
      newLicenseImages: [], // File[]

      detail: {},

      form: {
        device_type_id: null,
        asset_numbers: [{ asset_number_id: null, value: "" }],
        asset_name: "",
        serial_number: "",
        mst_employee_id: null,
        asset_user_name: "",
        asset_user_nik: "",
        mst_corporate_id: null,
        mst_site_id: null,
        mst_division_id: null,
        mst_position_id: null,
        asset_merk: "",
        asset_type: "",
        asset_processor: "",
        asset_ram: "",
        asset_hdd: "",
        asset_ssd: "",
        asset_operating_system: "",
        asset_office: "",
        asset_coa_os: "",
        asset_coa_office: "",
        asset_product_key_os: "",
        asset_product_key_office: "",
        asset_purchase_date: "",
        asset_receive_date: "",
        asset_handover_date: "",
        new_files: [],
        existing_files: [],
        file_path: null,
        existing_file: null,
        retired_file: null,
        existing_retired_file: null,
        status: 1,
        asset_condition: null,
        selectedEmpolyee: null,
      },
      // object { value, label, ... } agar Multiselect selalu bisa tampil di edit
      userSelect: null,
      isDragging: false,
      isDraggingRetired: false,
      manualUser: {
        name: "",
        nik: "",
        company: "",
        site: "",
        division: "",
        position: "",
      },
    };
  },
  watch: {
    "form.status"(newVal) {
      if (Number(newVal) !== 2) {
        this.form.retired_file = null;
      }
    },
  },
  computed: {
    pageTitle() {
      return { add: "Add Asset", edit: "Edit Asset", detail: "Detail Asset" }[
        this.mode
      ];
    },
    isRetired() {
      return Number(this.form.status) === 2;
    },

    submitLabel() {
      return this.mode === "add" ? "Save" : "Update";
    },

    isDetail() {
      return this.mode === "detail";
    },

    isEdit() {
      return this.mode === "edit";
    },

    isEditOrDetail() {
      return this.mode === "edit" || this.mode === "detail";
    },
    employeeOptions() {
      return (this.employeeList || []).map((e) => ({
        value: e.mst_employee_id,
        label: toUpper(e.mst_employee_name),
        mst_employee_id: e.mst_employee_id,
        mst_employee_name: e.mst_employee_name,
        mst_employee_nik: e.mst_employee_nik,
        mst_corporate_id: e.mst_corporate_id,
        mst_site_id: e.mst_site_id,
        mst_division_id: e.mst_division_id,
        mst_position_id: e.mst_position_id,
      }));
    },
    detailFileList() {
      const raw = this.detail?.file_path;
      if (!raw) return [];
      return String(raw).split("|").filter(Boolean);
    },
  },

  methods: {
    onUserSelectChange(val) {
      if (val === null || val === undefined || val === "") {
        this.form.mst_employee_id = null;
        this.form.asset_user_name = "";
        return;
      }

      const id =
        typeof val === "object" ? val?.value ?? val?.mst_employee_id : val;
      const emp = this.employeeList.find(
        (e) => String(e.mst_employee_id) === String(id),
      );

      if (emp) {
        this.form.mst_employee_id = emp.mst_employee_id;
        this.form.asset_user_name = toUpper(emp.mst_employee_name);
        this.form.asset_user_nik = emp.mst_employee_nik || "";
        this.form.mst_corporate_id = emp.mst_corporate_id ?? null;
        this.form.mst_site_id = emp.mst_site_id ?? null;
        this.form.mst_division_id = emp.mst_division_id ?? null;
        this.form.mst_position_id = emp.mst_position_id ?? null;
        return;
      }

      // ketik manual (string nama)
      const name = typeof val === "object" ? val?.label || val?.value : val;
      this.form.mst_employee_id = null;
      this.form.asset_user_name = toUpper(name);
    },

    addNewDocument(event) {
      const files = Array.from(event.target.files || []);
      const defaultType = this.documentTypeList[0]?.value ?? 5;
      files.forEach((file) => {
        this.newDocuments.push({
          file,
          document_type: defaultType,
        });
      });
      event.target.value = "";
    },
    removeNewDocument(idx) {
      this.newDocuments.splice(idx, 1);
    },
    removeExistingDocument(idx) {
      this.existingDocuments.splice(idx, 1);
    },
    addLicenseImages(event) {
      const files = Array.from(event.target.files || []);
      this.newLicenseImages.push(...files);
      event.target.value = "";
    },
    removeLicenseImage(idx) {
      this.newLicenseImages.splice(idx, 1);
    },

    async loadMasterData() {
      try {
        const [
          deviceType,
          employee,
          coa,
          condition,
          docType,
          corporate,
          site,
          division,
          position,
        ] = await Promise.all([
          deviceTypeController.getAll({ limit: 0, status: 1 }),
          employeeController.getAll({ limit: 0 }),
          parameterController.getAll({ param_id: 6, limit: 0, is_active: 1 }),
          parameterController.getAll({ param_id: 12, limit: 0, is_active: 1 }),
          parameterController.getAll({ param_id: 13, limit: 0, is_active: 1 }),
          corporateController.getAll({ limit: 0, status: 1 }),
          siteController.getAll({ limit: 0, status: 1 }),
          divisionController.getAll({ limit: 0 }),
          positionController.getAll({ limit: 0 }),
        ]);

        this.deviceTypeList = (deviceType.data || []).filter((dt) =>
          ["Laptop", "PC"].includes(dt.device_type_name),
        );
        this.employeeList = (employee.data || []).sort((a, b) =>
          (a.mst_employee_name || "").localeCompare(b.mst_employee_name || ""),
        );
        this.coaList = coa.data || [];
        this.conditionList = (condition.data || []).map((p) => ({
          value: Number(p.value),
          label: p.description,
        }));

        this.documentTypeList = (docType.data || [])
          .map((p) => ({
            value: Number(p.value),
            label: p.description,
          }))
          .filter((t) => t.value !== 4); // 4 = gambar lisensi

        this.corporateList = (corporate.data || [])
          .filter(isActive)
          .sort((a, b) =>
            (a.mst_corporate_name || "").localeCompare(
              b.mst_corporate_name || "",
            ),
          );
        this.siteList = (site.data || [])
          .filter(isActive)
          .sort((a, b) =>
            (a.mst_site_name || "").localeCompare(b.mst_site_name || ""),
          );

        this.divisionList = (division.data || []).sort((a, b) =>
          (a.mst_division_name || "").localeCompare(b.mst_division_name || ""),
        );
        this.positionList = (position.data || []).sort((a, b) =>
          (a.mst_position_name || "").localeCompare(b.mst_position_name || ""),
        );
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Gagal load master data",
        });
      }
    },

    async loadDetail() {
      try {
        const id = this.$route.params.id;

        const [res, assetNumbers] = await Promise.all([
          assetController.getById(id),
          assetController.getAssetNumbers(id),
        ]);

        if (this.mode === "detail") {
          this.detail = {
            ...res,
            asset_numbers: assetNumbers || [],
            documents: res.documents || [],
          };
          return;
        }

        Object.assign(this.form, {
          device_type_id: res.device_type_id,
          asset_numbers: assetNumbers.length
            ? assetNumbers.map((x) => ({
                asset_number_id: x.asset_number_id,
                value: x.asset_number,
              }))
            : [{ asset_number_id: null, value: "" }],
          asset_name: res.asset_name,
          serial_number: res.serial_number,
          mst_employee_id: res.mst_employee_id,
          asset_user_name: toUpper(
            res.asset_user_name || res.mst_employee_name || "",
          ),
          asset_user_nik: res.asset_user_nik || res.mst_employee_nik || "",
          mst_corporate_id: res.mst_corporate_id ?? null,
          mst_site_id: res.mst_site_id ?? null,
          mst_division_id: res.mst_division_id ?? null,
          mst_position_id: res.mst_position_id ?? null,
          asset_merk: res.asset_merk,
          asset_type: res.asset_type,
          asset_processor: res.asset_processor,
          asset_ram: res.asset_ram,
          asset_hdd: res.asset_hdd,
          asset_ssd: res.asset_ssd,
          asset_operating_system: res.asset_operating_system,
          asset_office: res.asset_office,
          asset_coa_os: res.asset_coa_os,
          asset_coa_office: res.asset_coa_office,
          asset_product_key_os: res.asset_product_key_os,
          asset_product_key_office: res.asset_product_key_office,
          asset_purchase_date:
            (res.asset_purchase_date || "").toString().split("T")[0] || "",
          asset_receive_date:
            (res.asset_receive_date || res.asset_received_date || "")
              .toString()
              .split("T")[0] || "",
          asset_handover_date:
            (res.asset_handover_date || "").toString().split("T")[0] || "",
          status: res.status,
          asset_condition:
            res.asset_condition != null ? Number(res.asset_condition) : null,
          file_path: null,
          existing_file: res.file_path || null,
          existing_files: res.file_path
            ? String(res.file_path).split("|").filter(Boolean)
            : [],
          new_files: [],
          retired_file: null,
          existing_retired_file: res.retired_file || null,
        });

        // —— Nama User: object mode supaya Multiselect selalu tampil ——
        const empName = toUpper(
          res.asset_user_name || res.mst_employee_name || "",
        );
        this.form.asset_user_name = empName || "";
        this.form.asset_user_nik =
          res.asset_user_nik || res.mst_employee_nik || "";
        this.form.mst_employee_id = res.mst_employee_id ?? null;

        if (res.mst_employee_id) {
          const empId = Number(res.mst_employee_id);
          const exists = (this.employeeList || []).some(
            (e) => Number(e.mst_employee_id) === empId,
          );
          if (!exists && empName) {
            this.employeeList = [
              {
                mst_employee_id: empId,
                mst_employee_name: empName,
                mst_employee_nik: this.form.asset_user_nik,
                mst_corporate_id: res.mst_corporate_id ?? null,
                mst_site_id: res.mst_site_id ?? null,
                mst_division_id: res.mst_division_id ?? null,
                mst_position_id: res.mst_position_id ?? null,
              },
              ...this.employeeList,
            ];
          }
          this.userSelect = {
            value: empId,
            label: empName || String(empId),
            mst_employee_id: empId,
            mst_employee_name: empName,
          };
        } else if (empName) {
          this.userSelect = {
            value: empName,
            label: empName,
          };
        } else {
          this.userSelect = null;
        }

        this.existingDocuments = res.documents || [];
        this.newDocuments = [];
        this.newLicenseImages = [];
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: "Gagal memuat detail asset",
        });
      }
    },
    goBack() {
      const from = this.$route.query.from;
      if (from) {
        this.$router.push(from);
      } else {
        this.$router.push("/asset/asset-it");
      }
    },

    addAssetNumber() {
      this.form.asset_numbers.push({ asset_number_id: null, value: "" });
    },

    removeAssetNumber(index) {
      if (this.form.asset_numbers.length === 1) return;
      const item = this.form.asset_numbers[index];
      if (item.asset_number_id) return;
      this.form.asset_numbers.splice(index, 1);
    },

    buildFormData() {
      const payload = new FormData();
      const skipKeys = [
        "file_path",
        "existing_file",
        "retired_file",
        "existing_retired_file",
        "asset_numbers",
        "selectedEmpolyee",
        "new_files",
        "existing_files",
      ];

      Object.keys(this.form).forEach((key) => {
        if (skipKeys.includes(key)) return;
        if (this.form[key] !== null && this.form[key] !== undefined) {
          payload.append(key, this.form[key]);
        }
      });
      if (this.form.asset_receive_date) {
        payload.append("asset_received_date", this.form.asset_receive_date);
      }

      (this.form.new_files || []).forEach((f) => {
        if (f instanceof File) payload.append("file_path", f);
      });
      payload.append(
        "existing_file",
        (this.form.existing_files || []).join("|"),
      );
      if (this.form.retired_file instanceof File) {
        payload.append("retired_file", this.form.retired_file);
      } else if (this.form.existing_retired_file !== null) {
        payload.append(
          "existing_retired_file",
          this.form.existing_retired_file,
        );
      }

      const validNumbers = this.form.asset_numbers.filter(
        (x) => x.value && x.value.trim(),
      );
      payload.append(
        "asset_numbers",
        JSON.stringify(
          validNumbers.map((x) => ({
            asset_number_id: x.asset_number_id,
            asset_number: x.value,
          })),
        ),
      );

      const docTypes = [];
      (this.newDocuments || []).forEach((d) => {
        if (d.file instanceof File) {
          payload.append("documents", d.file);
          docTypes.push(d.document_type || 5);
        }
      });
      payload.append("document_types", JSON.stringify(docTypes));

      (this.newLicenseImages || []).forEach((f) => {
        if (f instanceof File) payload.append("license_images", f);
      });

      const keepIds = (this.existingDocuments || [])
        .map((d) => d.asset_document_id)
        .filter(Boolean);
      payload.append("keep_document_ids", JSON.stringify(keepIds));

      return payload;
    },

    async saveData() {
      this.form.asset_user_name = toUpper(this.form.asset_user_name);

      if (!this.form.device_type_id) {
        Swal.fire({
          icon: "warning",
          title: "Warning",
          text: "Device type wajib diisi",
        });
        return;
      }
      if (!this.form.asset_user_name) {
        Swal.fire({
          icon: "warning",
          title: "Warning",
          text: "Nama User wajib diisi",
        });
        return;
      }

      this.isLoading = true;
      Swal.fire({
        title: "Menyimpan Dokumen...",
        html: "Mohon tunggu, jangan tutup atau refresh halaman ini.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      try {
        const payload = this.buildFormData();

        if (this.mode === "add") {
          await assetController.create(payload);
        } else {
          await assetController.update(this.$route.params.id, payload);
        }

        Swal.fire({
          icon: "success",
          title: "Berhasil",
          text:
            this.mode === "add"
              ? "Asset berhasil ditambahkan"
              : "Asset berhasil diupdate",
        });

        this.goBack();
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text: error.response?.data?.message || "Terjadi kesalahan",
        });
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(date) {
      if (!date) return "-";
      const s = String(date);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return new Intl.DateTimeFormat("id-ID", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        }).format(d);
      }
      return new Intl.DateTimeFormat("id-ID", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }).format(new Date(date));
    },
    handleFileUpload(event) {
      const files = Array.from(event.target.files || []).filter(
        (f) =>
          f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
      );
      this.form.new_files = [...(this.form.new_files || []), ...files];
      event.target.value = "";
    },

    handleDrop(e) {
      this.isDragging = false;
      const files = Array.from(e.dataTransfer.files || []).filter(
        (f) =>
          f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"),
      );
      this.form.new_files = [...(this.form.new_files || []), ...files];
    },

    removeNewFile(index) {
      this.form.new_files.splice(index, 1);
    },

    removeExistingFile(index) {
      this.form.existing_files.splice(index, 1);
    },

    getFileName(key) {
      if (!key) return "";
      return key.split("/").pop().split("\\").pop();
    },

    async downloadFile(key) {
      if (!key) return;
      try {
        const res = await assetController.getSignedUrl(key);
        if (res?.url) window.open(res.url, "_blank");
        else Swal.fire("Error", "Gagal mendapatkan link download", "error");
      } catch (err) {
        Swal.fire("Error", "Gagal mengunduh file", "error");
      }
    },
    handleRetiredFileUpload(event) {
      const file = event.target.files[0] || null;
      if (file) this.form.retired_file = file;
      event.target.value = "";
    },

    handleRetiredDrop(e) {
      this.isDraggingRetired = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type === "application/pdf") {
        this.form.retired_file = file;
      }
    },

    removeNewRetiredFile() {
      this.form.retired_file = null;
    },

    removeExistingRetiredFile() {
      this.form.existing_retired_file = "";
    },
  },

  async mounted() {
    await this.loadMasterData();
    if (this.isEditOrDetail) await this.loadDetail();
  },
};
